import {
  CustomFieldFilter,
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  GetStudentsQueryFilters,
  MedicalNeedFilter,
  STUDENTS_ARRANGE_BY_FILTER_KEYS,
} from '@schooly/api';
import {
  getSelectedItemsWithGrouping,
  SelectedItem,
  SelectedItemWithGrouping,
} from '@schooly/components/filters';
import {
  Genders,
  Nationalities,
  PROPERTIES_TEXT_IDS,
  SchoolPropertyType,
  SchoolUserRole,
} from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../hooks/useSchool';

export type UseStudentsFiltersProps = {
  schoolId: string;
  filters: GetStudentsQueryFilters;
  onSetFilters: (filters: GetStudentsQueryFilters) => void;
};

export const useStudentsFilters = ({
  schoolId,
  filters,
  onSetFilters,
}: UseStudentsFiltersProps) => {
  const ageGroups = useAgeGroups({
    schoolId: schoolId,
    userType: SchoolUserRole.Student,
  });
  const { getAgeGroupById, schoolLevelsWithAgeGroupsMap } = ageGroups;
  const { hasHouses } = useSchool();
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const onUpdateCustomFieldFilter = (v: CustomFieldFilter[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.CustomFields]: v,
      });
    };

    const onSetMedicalNeeds = (v: MedicalNeedFilter | null) => {
      onSetFilters({
        ...filters,
        [FilterKeys.MedicalNeeds]: v ?? undefined,
      });
    };

    const updateFilter =
      (key: FilterKeys.House | FilterKeys.LeavingReason | FilterKeys.AgeGroup) => (v: string[]) => {
        onSetFilters({
          ...filters,
          [key]: v,
        });
      };
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetNationality = (v: Nationalities[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Nationality]: v,
      });
    };
    const onSetGender = (v: Genders[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Gender]: v,
      });
    };

    const onSetStatusId = (v: string[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Status]: v,
        [FilterKeys.Expired]: false,
      });
    };

    const onSetNoStatus = (status = true) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Expired]: status,
        [FilterKeys.Status]: [],
      });
    };

    return {
      onSetDate: (v: Date) =>
        onSetFilters({ ...filters, [FilterKeys.Date]: [format(v, DEFAULT_DATE_FORMAT_FNS)] }),
      onSetGender,
      onSetNationality,
      onSetHouseId: updateFilter(FilterKeys.House),
      onClearHouse: clearFilter(FilterKeys.House),
      onSetAgeGroup: updateFilter(FilterKeys.AgeGroup),
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
      onSetStatusId,
      onSetNoStatus,
      onSetLeavingReasonId: updateFilter(FilterKeys.LeavingReason),
      onSetMedicalNeeds,
      onUpdateCustomFieldFilter,
      // onClearLeavingReason: clearFilter(FilterKeys.LeavingReason),
      onClearStatuses: () => {
        onSetFilters({
          ...filters,
          [FilterKeys.Status]: [],
          [FilterKeys.Expired]: false,
        });
      },
      onClearNoStatus: clearFilter(FilterKeys.Expired),
      onClearNationality: clearFilter(FilterKeys.Nationality),
      onClearGender: clearFilter(FilterKeys.Gender),
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date?.[0];
  const filtersStatus = filters.status;
  const filtersNoStatus = filters.expired;
  const filtersNationality = filters.nationality;
  const filtersGender = filters.gender;
  const filtersMedicalNeeds = filters.medical_needs;
  const filtersHouse = filters.house;
  const filtersAgeGroup = filters.age_group;
  const filtersCustomFields = filters.custom_fields;
  const noStatusFilterSelected = Boolean(filtersNoStatus);
  const noStatusOption = {
    id: FilterKeys.Expired,
    name: formatMessage({ id: PROPERTIES_TEXT_IDS[FilterKeys.Expired] }),
    type: SchoolPropertyType.Status,
  };

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-Date' }),
    [FilterKeys.Status]: formatMessage({ id: 'schoolProperty-Status' }),
    [FilterKeys.AgeGroup]: formatMessage({ id: 'schoolProperty-AgeGroup' }),
    [FilterKeys.House]: formatMessage({ id: 'schoolProperty-House' }),
    [FilterKeys.Gender]: formatMessage({ id: 'peopleDetail-Gender' }),
    [FilterKeys.Nationality]: formatMessage({ id: 'peopleDetail-Nationality' }),
    [FilterKeys.MedicalNeeds]: formatMessage({ id: 'peopleDetail-MedicalNeeds' }),
    [FilterKeys.CustomFields]: formatMessage({ id: 'peopleDetail-AdditionalInformation' }),
    arrangeBy: formatMessage({ id: 'filter-ArrangeBy' }),
  };

  const arrangeByDropdownOptions = hasHouses
    ? STUDENTS_ARRANGE_BY_FILTER_KEYS
    : STUDENTS_ARRANGE_BY_FILTER_KEYS.filter((k) => k !== FilterKeys.House);

  const selectedItemsForAgeGroups: SelectedItemWithGrouping[] = useMemo(() => {
    const selectedAgeGroups =
      filtersAgeGroup?.reduce<SelectedItem[]>((acc, id) => {
        const ageGroup = getAgeGroupById(id);

        return ageGroup
          ? [
              ...acc,
              {
                id: ageGroup.id,
                groupId: ageGroup.level_id,
              },
            ]
          : acc;
      }, []) ?? [];

    return getSelectedItemsWithGrouping(selectedAgeGroups, schoolLevelsWithAgeGroupsMap);
  }, [filtersAgeGroup, schoolLevelsWithAgeGroupsMap, getAgeGroupById]);

  return {
    ...ageGroups,
    ...actions,
    labels,
    hasHouses,
    arrangeByDropdownOptions,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersAgeGroup,
    filtersStatus,
    filtersNationality,
    filtersGender,
    filtersMedicalNeeds,
    filtersHouse,
    filtersNoStatus,
    filtersCustomFields,
    noStatusFilterSelected,
    noStatusOption,
  };
};
